import { isNil, toNumber } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';

import { Cell, Container, Grid } from 'components/Grid';
import { SecondaryParagraphHeading } from 'components/Headings';
import { largeUp } from 'constants/mediaQueries';
import { PAGINATION_CONFIG, SEARCH_ORIGINATIONS } from 'constants/search';
import SearchResultsContainer from 'containers/SearchResultsContainer';
import Theme from 'styles/theme';
import DataLayer from 'utils/DataLayer';
import I18n from 'utils/I18n';
import Rudderstack from 'utils/Rudderstack';
import StorefrontUtil from 'utils/Storefront';
import { parseQueryString, queryParamSearchQuery, replaceHistory } from 'utils/Url';

import { SearchBox } from './SearchBox';
import { SearchPageSentinel } from './SearchPageSentinel';
import { Stats } from './Stats';
import { useShopifySearch } from '../../hooks/useShopifySearch';

const transformData = (searchResultState, searchValueState) => ({
  searchResultsItems: searchResultState?.searchItemsDisplayed?.map(({ node }) => ({
    id: toNumber(StorefrontUtil.convertGidtoId(node.id)),
    name: node.title,
    color: node?.colorName?.value,
    masterName: node.masterName?.value,
    handle: node.handle,
  })),
  searchResultsCount: searchResultState?.searchTotalItems,
  query: searchValueState,
});

const SearchPage = ({ trackSearchNavigation }) => {
  const isLargeUp = useMediaQuery({ query: largeUp });
  const { searchValueState, setSearchValueState, searchResultState, searchFetchNextPage } =
    useShopifySearch({
      paginationPageSize: isLargeUp
        ? PAGINATION_CONFIG.ELEMENTS_PER_PAGE_MEDIUM_UP
        : PAGINATION_CONFIG.ELEMENTS_PER_PAGE_SMALL,
    });

  const updateUrlReplaceHistory = updatedSearchQuery => {
    if (!isNil(replaceHistory) && updatedSearchQuery != '') {
      const currentUrlParams = parseQueryString();

      replaceHistory(
        {
          ...currentUrlParams,
          query: encodeURI(updatedSearchQuery),
        },
        null
      );
    } else {
      replaceHistory('', null);
    }
  };

  const onSearchValueStateChange = searchValueUpdated => {
    const newSearchValue = decodeURIComponent(searchValueUpdated);

    setSearchValueState(newSearchValue);
    updateUrlReplaceHistory(newSearchValue);
  };

  const onPageSentinelEvent = () => {
    searchFetchNextPage();
  };

  /**
   * Debounced on the child component where is hooked
   */
  const onChangeSearchInput = queryInput => {
    const querySearch = isNil(queryInput) ? '' : queryInput;

    onSearchValueStateChange(querySearch);
  };

  useEffect(() => {
    const querySearchParam = queryParamSearchQuery();
    const querySearchToFetch = querySearchParam ? decodeURIComponent(querySearchParam) : '';

    onSearchValueStateChange(querySearchToFetch);

    window.scrollTo({ top: 0, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchResultState?.searchTotalItems > 0 && searchValueState != '') {
      Rudderstack.productsSearched({
        query: searchValueState,
        searchResultsCount: searchResultState?.searchTotalItems,
        searchSource: window.location.href,
        searchEntry: SEARCH_ORIGINATIONS.SEARCH_PAGE,
      });
      DataLayer.pushEvent({
        event: 'storeSearch',
        searchData: transformData(searchResultState, searchValueState),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultState]);

  return (
    <div className="SearchPage">
      <Container>
        <Grid isCentered>
          <Cell small={{ columns: 12 }} medium={{ columns: 11 }} largeUp={{ columns: 10 }}>
            <div className="SearchPage__Header">
              <SecondaryParagraphHeading>
                {I18n.localize('searchBar.searchHeading')}
              </SecondaryParagraphHeading>

              <div className="SearchPage__search-box">
                <SearchBox
                  trackSearchNavigation={trackSearchNavigation}
                  currentSearchValue={searchValueState}
                  onSearchChange={onChangeSearchInput}
                />
              </div>

              <div className="SearchPage__search-stats">
                <Stats
                  currentSearchValue={searchValueState}
                  searchResultStats={{
                    totalItemsSearched: searchResultState?.searchTotalItems,
                    errorOnSearch: searchResultState?.searchHasError,
                  }}
                />
              </div>
            </div>

            {searchResultState?.searchTotalItemsDisplayed > 0 && (
              <div className="SearchPage__search-results">
                <SearchResultsContainer
                  searchResults={searchResultState?.searchItemsDisplayed}
                  trackSearchNavigation={trackSearchNavigation}
                />
                <SearchPageSentinel
                  showSentinel={searchResultState?.paginationHasMore}
                  handleSentinelEvent={onPageSentinelEvent}
                />
              </div>
            )}
          </Cell>
        </Grid>
      </Container>

      <style jsx>{`
        .SearchPage {
          background-color: ${Theme.get('colors-black-10')};
          padding-top: ${Theme.get('spacing') * 3}px;
          min-height: ${Theme.get('page-height-px')};
          padding-bottom: ${Theme.get('spacing') * 2}px;
        }

        .SearchPage__Header {
          margin-bottom: ${Theme.get('spacing') * 6}px;
        }

        .SearchPage__search-box {
          margin: ${Theme.get('spacing') * 2}px 0;
        }

        .SearchPage__search-box {
          display: block;
        }

        @media ${largeUp} {
          .SearchPage {
            padding-top: ${Theme.get('spacing') * 12}px;
            background-color: ${Theme.get('colors-white')};
          }
          .SearchPage__search-results {
            padding-bottom: ${Theme.get('spacing') * 18}px;
          }
        }
      `}</style>
    </div>
  );
};

SearchPage.propTypes = {
  trackSearchNavigation: PropTypes.func,
  trackProductsSearched: PropTypes.func,
};

SearchPage.defaultProps = {
  trackSearchNavigation: () => {},
  trackProductsSearched: () => {},
};

export default SearchPage;
